// ********* CardUserInputModal - userinputmodal.jsx ********
// autor: W de Paula / MVinfo
// data: 18/7/24 | 17h00
// alterações e versões:
// -- data: 4/10/25w | 12h22 - claude.ia
// **************************************************

import React, { useEffect, useState, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import InputMask from 'react-input-mask';
import { getUserInfo, saveUserInfo } from '../../../../Services/getsaveuserinfo';
import Swal from 'sweetalert2';

const CardUserInputModal = ({ show, onHide, handleSave, profissionalNome, servicoNome, servicoValor, servicoDataHora }) => {
    const [celular, setCelular] = useState('');
    const [nome, setNome] = useState('');
    const [obs, setObs] = useState('');
    const [errors, setErrors] = useState({ celular: false, nome: false });
    const [touched, setTouched] = useState({ celular: false, nome: false });

    const celularRef = useRef(null);
    const nomeRef = useRef(null);

    // Carregar dados do localStorage quando o componente monta
    useEffect(() => {
        const userInfo = getUserInfo();
        if (userInfo) {
            setNome(userInfo.nome || '');
            setCelular(userInfo.celular || '');
            setObs(userInfo.obs || '');
        }
    }, []);

    // Foco automático no campo celular quando o modal abre
    useEffect(() => {
        if (show && celularRef.current) {
            setTimeout(() => {
                // InputMask requer acesso ao input interno
                const input = celularRef.current.inputElement || celularRef.current;
                if (input && input.focus) {
                    input.focus();
                }
            }, 100);
        }
    }, [show]);

    // Validação em tempo real
    const validateCelular = (value) => {
        const cleaned = value.replace(/\D/g, '');
        return cleaned.length >= 10;
    };

    const validateNome = (value) => {
        return value.trim().length >= 3;
    };

    const handleCelularChange = (e) => {
        const value = e.target.value;
        setCelular(value);
        if (touched.celular) {
            setErrors(prev => ({ ...prev, celular: !validateCelular(value) }));
        }
    };

    const handleNomeChange = (e) => {
        const value = e.target.value;
        setNome(value);
        if (touched.nome) {
            setErrors(prev => ({ ...prev, nome: !validateNome(value) }));
        }
    };

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        if (field === 'celular') {
            setErrors(prev => ({ ...prev, celular: !validateCelular(celular) }));
        } else if (field === 'nome') {
            setErrors(prev => ({ ...prev, nome: !validateNome(nome) }));
        }
    };

    const handleSaveClick = () => {
        const celularValid = validateCelular(celular);
        const nomeValid = validateNome(nome);

        setTouched({ celular: true, nome: true });
        setErrors({ celular: !celularValid, nome: !nomeValid });

        if (!celularValid || !nomeValid) {
            Swal.fire({
                icon: 'warning',
                title: 'Atenção',
                html: '<div style="text-align: left;">Por favor, preencha:<br/>• Celular (mínimo 10 dígitos)<br/>• Nome (mínimo 3 caracteres)</div>',
                confirmButtonColor: '#0d6efd'
            }).then(() => {
                // Foca no primeiro campo com erro após fechar o alerta
                if (!celularValid && celularRef.current) {
                    // InputMask requer acesso ao input interno
                    const input = celularRef.current.inputElement || celularRef.current;
                    if (input && input.focus) {
                        input.focus();
                    }
                } else if (!nomeValid && nomeRef.current && nomeRef.current.focus) {
                    nomeRef.current.focus();
                }
            });
            return;
        }

        saveUserInfo(nome, celular);
        handleSave({ nome, celular, obs });
        onHide();
    };

    const handleClose = () => {
        setErrors({ celular: false, nome: false });
        setTouched({ celular: false, nome: false });
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="md" centered>
            <Modal.Header closeButton className="border-0 pb-2">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-calendar-check me-2" style={{ fontSize: '1.5rem', color: '#0d6efd' }}></i>
                        <span>Confirmar Agendamento</span>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2">
                {/* Resumo do Agendamento */}
                <div style={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px',
                    border: '1px solid #e9ecef'
                }}>
                    <h6 style={{ color: '#495057', marginBottom: '12px', fontWeight: '600' }}>
                        📋 Resumo do Agendamento
                    </h6>
                    <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        <div className="mb-2">
                            <strong style={{ color: '#495057' }}>Profissional:</strong> {profissionalNome}
                        </div>
                        <div className="mb-2">
                            <strong style={{ color: '#495057' }}>Serviço:</strong> {servicoNome}
                        </div>
                        <div className="mb-2">
                            <strong style={{ color: '#495057' }}>Data/Hora:</strong> {servicoDataHora} hs
                        </div>
                        <div>
                            <strong style={{ color: '#495057' }}>Valor:</strong> <span style={{ color: '#198754', fontWeight: '600' }}>{servicoValor}</span>
                        </div>
                    </div>
                </div>

                {/* Texto LGPD discreto */}
                <div style={{
                    fontSize: '0.85rem',
                    color: '#6c757d',
                    padding: '12px',
                    backgroundColor: '#e7f3ff',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    borderLeft: '3px solid #0d6efd'
                }}>
                    <i className="bi bi-info-circle me-2"></i>
                    Seus dados serão compartilhados com o estabelecimento para contato e cadastro no sistema Agendex.
                </div>

                {/* Campos do Formulário */}
                <div className="mb-3">
                    <label htmlFor="celular" className="form-label" style={{ fontWeight: '600', color: '#495057' }}>
                        <i className="bi bi-whatsapp me-2" style={{ color: '#25D366' }}></i>
                        WhatsApp <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <InputMask
                        mask="(99)99999-9999"
                        formatChars={{
                            '9': '[0-9]',
                        }}
                        maskChar={null}
                        type="tel"
                        id="celular"
                        className={`form-control ${errors.celular ? 'is-invalid' : touched.celular && celular ? 'is-valid' : ''}`}
                        placeholder="(00)00000-0000"
                        value={celular}
                        onChange={handleCelularChange}
                        onBlur={() => handleBlur('celular')}
                        ref={celularRef}
                        style={{
                            borderWidth: '2px',
                            fontSize: '1rem',
                            padding: '10px 12px',
                            backgroundColor: errors.celular ? '#fff5f5' : '#ffffff'
                        }}
                    />
                    {errors.celular && (
                        <div className="invalid-feedback d-block">
                            <i className="bi bi-exclamation-circle me-1"></i>
                            Digite um celular válido com DDD
                        </div>
                    )}
                </div>

                <div className="mb-3">
                    <label htmlFor="nome" className="form-label" style={{ fontWeight: '600', color: '#495057' }}>
                        <i className="bi bi-person me-2" style={{ color: '#0d6efd' }}></i>
                        Seu Nome <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                        type="text"
                        id="nome"
                        className={`form-control ${errors.nome ? 'is-invalid' : touched.nome && nome ? 'is-valid' : ''}`}
                        placeholder="Digite seu nome completo"
                        value={nome}
                        onChange={handleNomeChange}
                        onBlur={() => handleBlur('nome')}
                        ref={nomeRef}
                        minLength="3"
                        style={{
                            borderWidth: '2px',
                            fontSize: '1rem',
                            padding: '10px 12px',
                            backgroundColor: errors.nome ? '#fff5f5' : '#ffffff'
                        }}
                    />
                    {errors.nome && (
                        <div className="invalid-feedback d-block">
                            <i className="bi bi-exclamation-circle me-1"></i>
                            Nome deve ter no mínimo 3 caracteres
                        </div>
                    )}
                </div>

                <div className="mb-3">
                    <label htmlFor="obs" className="form-label" style={{ fontWeight: '600', color: '#495057' }}>
                        <i className="bi bi-chat-left-text me-2" style={{ color: '#6c757d' }}></i>
                        Observação <span style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: 'normal' }}>(opcional)</span>
                    </label>
                    <textarea
                        id="obs"
                        className="form-control"
                        placeholder="Alguma observação ou pedido especial?"
                        value={obs}
                        onChange={(e) => setObs(e.target.value)}
                        rows="3"
                        style={{
                            borderWidth: '2px',
                            borderColor: '#ced4da',
                            fontSize: '0.95rem',
                            padding: '10px 12px',
                            resize: 'none'
                        }}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button 
                    variant="outline-secondary" 
                    onClick={handleClose}
                    style={{ 
                        borderWidth: '2px',
                        fontWeight: '600',
                        padding: '10px 24px'
                    }}
                >
                    <i className="bi bi-x-circle me-2"></i>
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSaveClick}
                    style={{ 
                        fontWeight: '600',
                        padding: '10px 24px',
                        boxShadow: '0 2px 4px rgba(13, 110, 253, 0.2)'
                    }}
                >
                    <i className="bi bi-check-circle me-2"></i>
                    Confirmar Agendamento
                </Button>
                {/* {console.log('confirmar agendamento')} */}
            </Modal.Footer>
        </Modal>
    );
};

export default CardUserInputModal;


// import React, { useEffect, useState, useRef } from 'react';
// import { Modal, Button } from 'react-bootstrap';
// import InputMask from 'react-input-mask';
// import { getUserInfo, saveUserInfo } from '../../../../Services/getsaveuserinfo';
// import Swal from 'sweetalert2';

// const CardUserInputModal = ({ show, onHide, handleSave, profissionalNome, servicoNome, servicoValor, servicoDataHora }) => {
//     const [celular, setCelular] = useState('');
//     const [nome, setNome] = useState('');
//     const [obs, setObs] = useState('');
//     const [errors, setErrors] = useState({ celular: false, nome: false });
//     const [touched, setTouched] = useState({ celular: false, nome: false });

//     const celularRef = useRef(null);
//     const nomeRef = useRef(null);

//     // Carregar dados do localStorage quando o componente monta
//     useEffect(() => {
//         const userInfo = getUserInfo();
//         if (userInfo) {
//             setNome(userInfo.nome || '');
//             setCelular(userInfo.celular || '');
//             setObs(userInfo.obs || '');
//         }
//     }, []);

//     // Foco automático no campo celular quando o modal abre
//     useEffect(() => {
//         if (show && celularRef.current) {
//             setTimeout(() => {
//                 // InputMask requer acesso ao input interno
//                 const input = celularRef.current.inputElement || celularRef.current;
//                 if (input && input.focus) {
//                     input.focus();
//                 }
//             }, 100);
//         }
//     }, [show]);

//     // Validação em tempo real
//     const validateCelular = (value) => {
//         const cleaned = value.replace(/\D/g, '');
//         return cleaned.length >= 10;
//     };

//     const validateNome = (value) => {
//         return value.trim().length >= 3;
//     };

//     const handleCelularChange = (e) => {
//         const value = e.target.value;
//         setCelular(value);
//         if (touched.celular) {
//             setErrors(prev => ({ ...prev, celular: !validateCelular(value) }));
//         }
//     };

//     const handleNomeChange = (e) => {
//         const value = e.target.value;
//         setNome(value);
//         if (touched.nome) {
//             setErrors(prev => ({ ...prev, nome: !validateNome(value) }));
//         }
//     };

//     const handleBlur = (field) => {
//         setTouched(prev => ({ ...prev, [field]: true }));
//         if (field === 'celular') {
//             setErrors(prev => ({ ...prev, celular: !validateCelular(celular) }));
//         } else if (field === 'nome') {
//             setErrors(prev => ({ ...prev, nome: !validateNome(nome) }));
//         }
//     };

//     const handleSaveClick = () => {
//         const celularValid = validateCelular(celular);
//         const nomeValid = validateNome(nome);

//         setTouched({ celular: true, nome: true });
//         setErrors({ celular: !celularValid, nome: !nomeValid });

//         if (!celularValid || !nomeValid) {
//             Swal.fire({
//                 icon: 'warning',
//                 title: 'Atenção',
//                 html: '<div style="text-align: left;">Por favor, preencha:<br/>• Celular (mínimo 10 dígitos)<br/>• Nome (mínimo 3 caracteres)</div>',
//                 confirmButtonColor: '#0d6efd'
//             }).then(() => {
//                 // Foca no primeiro campo com erro após fechar o alerta
//                 if (!celularValid && celularRef.current) {
//                     // InputMask requer acesso ao input interno
//                     const input = celularRef.current.inputElement || celularRef.current;
//                     if (input && input.focus) {
//                         input.focus();
//                     }
//                 } else if (!nomeValid && nomeRef.current && nomeRef.current.focus) {
//                     nomeRef.current.focus();
//                 }
//             });
//             return;
//         }

//         saveUserInfo(nome, celular);
//         handleSave({ nome, celular, obs });
//         onHide();
//     };

//     const handleClose = () => {
//         setErrors({ celular: false, nome: false });
//         setTouched({ celular: false, nome: false });
//         onHide();
//     };

//     return (
//         <Modal show={show} onHide={handleClose} size="md" centered>
//             <Modal.Header closeButton className="border-0 pb-2">
//                 <Modal.Title className="w-100">
//                     <div className="d-flex align-items-center">
//                         <i className="bi bi-calendar-check me-2" style={{ fontSize: '1.5rem', color: '#0d6efd' }}></i>
//                         <span>Confirmar Agendamento</span>
//                     </div>
//                 </Modal.Title>
//             </Modal.Header>
//             <Modal.Body className="pt-2">
//                 {/* Resumo do Agendamento */}
//                 <div style={{
//                     backgroundColor: '#f8f9fa',
//                     borderRadius: '8px',
//                     padding: '16px',
//                     marginBottom: '20px',
//                     border: '1px solid #e9ecef'
//                 }}>
//                     <h6 style={{ color: '#495057', marginBottom: '12px', fontWeight: '600' }}>
//                         📋 Resumo do Agendamento
//                     </h6>
//                     <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
//                         <div className="mb-2">
//                             <strong style={{ color: '#495057' }}>Profissional:</strong> {profissionalNome}
//                         </div>
//                         <div className="mb-2">
//                             <strong style={{ color: '#495057' }}>Serviço:</strong> {servicoNome}
//                         </div>
//                         <div className="mb-2">
//                             <strong style={{ color: '#495057' }}>Data/Hora:</strong> {servicoDataHora} hs
//                         </div>
//                         <div>
//                             <strong style={{ color: '#495057' }}>Valor:</strong> <span style={{ color: '#198754', fontWeight: '600' }}>{servicoValor}</span>
//                         </div>
//                     </div>
//                 </div>

//                 {/* Texto LGPD discreto */}
//                 <div style={{
//                     fontSize: '0.85rem',
//                     color: '#6c757d',
//                     padding: '12px',
//                     backgroundColor: '#e7f3ff',
//                     borderRadius: '6px',
//                     marginBottom: '20px',
//                     borderLeft: '3px solid #0d6efd'
//                 }}>
//                     <i className="bi bi-info-circle me-2"></i>
//                     Seus dados serão compartilhados com o estabelecimento para contato e cadastro no sistema Agendex.
//                 </div>

//                 {/* Campos do Formulário */}
//                 <div className="mb-3">
//                     <label htmlFor="celular" className="form-label" style={{ fontWeight: '600', color: '#495057' }}>
//                         <i className="bi bi-whatsapp me-2" style={{ color: '#25D366' }}></i>
//                         WhatsApp <span style={{ color: '#dc3545' }}>*</span>
//                     </label>
//                     {/* <InputMask
//                         mask={celular.replace(/\D/g, '').length <= 10 ? "(99)9999-9999" : "(99)99999-9999"}
//                     /> */}
//                      <InputMask
//                         mask="(99)99999-9999"
//                         formatChars={{
//                             '9': '[0-9]',
//                         }}
//                         maskChar={null}
//                         type="tel"
//                         id="celular"
//                         className={`form-control ${errors.celular ? 'is-invalid' : touched.celular && celular ? 'is-valid' : ''}`}
//                         placeholder="(00)00000-0000"
//                         value={celular}
//                         onChange={handleCelularChange}
//                         onBlur={() => handleBlur('celular')}
//                         ref={celularRef}
//                         style={{
//                             borderWidth: '2px',
//                             fontSize: '1rem',
//                             padding: '10px 12px',
//                             backgroundColor: errors.celular ? '#fff5f5' : '#ffffff'
//                         }}
//                     />
//                     {errors.celular && (
//                         <div className="invalid-feedback d-block">
//                             <i className="bi bi-exclamation-circle me-1"></i>
//                             Digite um celular válido com DDD
//                         </div>
//                     )}
//                 </div>

//                 <div className="mb-3">
//                     <label htmlFor="nome" className="form-label" style={{ fontWeight: '600', color: '#495057' }}>
//                         <i className="bi bi-person me-2" style={{ color: '#0d6efd' }}></i>
//                         Seu Nome <span style={{ color: '#dc3545' }}>*</span>
//                     </label>
//                     <input
//                         type="text"
//                         id="nome"
//                         className={`form-control ${errors.nome ? 'is-invalid' : touched.nome && nome ? 'is-valid' : ''}`}
//                         placeholder="Digite seu nome completo"
//                         value={nome}
//                         onChange={handleNomeChange}
//                         onBlur={() => handleBlur('nome')}
//                         ref={nomeRef}
//                         minLength="3"
//                         style={{
//                             borderWidth: '2px',
//                             fontSize: '1rem',
//                             padding: '10px 12px',
//                             backgroundColor: errors.nome ? '#fff5f5' : '#ffffff'
//                         }}
//                     />
//                     {errors.nome && (
//                         <div className="invalid-feedback d-block">
//                             <i className="bi bi-exclamation-circle me-1"></i>
//                             Nome deve ter no mínimo 3 caracteres
//                         </div>
//                     )}
//                 </div>

//                 <div className="mb-3">
//                     <label htmlFor="obs" className="form-label" style={{ fontWeight: '600', color: '#495057' }}>
//                         <i className="bi bi-chat-left-text me-2" style={{ color: '#6c757d' }}></i>
//                         Observação <span style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: 'normal' }}>(opcional)</span>
//                     </label>
//                     <textarea
//                         id="obs"
//                         className="form-control"
//                         placeholder="Alguma observação ou pedido especial?"
//                         value={obs}
//                         onChange={(e) => setObs(e.target.value)}
//                         rows="3"
//                         style={{
//                             borderWidth: '2px',
//                             borderColor: '#ced4da',
//                             fontSize: '0.95rem',
//                             padding: '10px 12px',
//                             resize: 'none'
//                         }}
//                     />
//                 </div>
//             </Modal.Body>
//             <Modal.Footer className="border-0 pt-0">
//                 <Button 
//                     variant="outline-secondary" 
//                     onClick={handleClose}
//                     style={{ 
//                         borderWidth: '2px',
//                         fontWeight: '600',
//                         padding: '10px 24px'
//                     }}
//                 >
//                     <i className="bi bi-x-circle me-2"></i>
//                     Cancelar
//                 </Button>
//                 <Button 
//                     variant="primary" 
//                     onClick={handleSaveClick}
//                     style={{ 
//                         fontWeight: '600',
//                         padding: '10px 24px',
//                         boxShadow: '0 2px 4px rgba(13, 110, 253, 0.2)'
//                     }}
//                 >
//                     <i className="bi bi-check-circle me-2"></i>
//                     Confirmar Agendamento
//                 </Button>
//             </Modal.Footer>
//         </Modal>
//     );
// };

// export default CardUserInputModal;